import type { ReactNode } from 'react';
import { EmptyState } from '../empty-state';

export type DataTableState = 'loading' | 'ready' | 'empty' | 'no-results' | 'error';

export interface DataTableStatesProps {
  state: Exclude<DataTableState, 'loading' | 'ready'>;
  columns: number;
  onRetry?: () => void;
  onClearFilters?: () => void;
  emptyTitle: string;
  emptyDescription: string;
  emptyAction?: ReactNode;
  /**
   * What failed, in the view's own words.
   *
   * Symmetric with `emptyTitle`: a module that shows the same list twice — a
   * table and a grouped view — cannot have one of them say «no pudimos cargar
   * los datos» while the other says «las tareas» about the same broken query.
   */
  errorTitle?: string;
  /** What actually failed. The API contract writes it for the user (§1.6). */
  errorDescription?: string;
}

/**
 * Error, empty and no-results live inside the table body so the header — and
 * therefore the column controls — stays where the user left it.
 */
export function DataTableStates({
  state,
  columns,
  onRetry,
  onClearFilters,
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorTitle,
  errorDescription,
}: DataTableStatesProps) {
  return (
    <tbody>
      <tr>
        <td colSpan={columns}>
          {state === 'error' && (
            <EmptyState
              variant="error"
              title={errorTitle ?? 'No pudimos cargar los datos'}
              // A view that knows which of its queries broke says so; the
              // generic line is the fallback, not the only thing on offer.
              description={errorDescription ?? 'La consulta falló. Puede ser algo momentáneo.'}
              {...(onRetry ? { onRetry } : {})}
            />
          )}
          {state === 'no-results' && (
            <EmptyState
              variant="no-results"
              title="Nada coincide con estos filtros"
              description="Prueba con otros criterios o quita alguno de los filtros activos."
              {...(onClearFilters ? { onClearFilters } : {})}
            />
          )}
          {state === 'empty' && (
            <EmptyState
              variant="empty"
              title={emptyTitle}
              description={emptyDescription}
              {...(emptyAction ? { action: emptyAction } : {})}
            />
          )}
        </td>
      </tr>
    </tbody>
  );
}
